import { ProgressCard } from "../common/ProgressCard";
import { Textarea } from "@/components/ui/textarea";
import { Info } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { cn } from "@/lib/utils"; // used by ChoiceCard

interface RiskManagementCardProps {
  onCollapse: () => void;
}

const TEXTAREA_CLASS =
  "min-h-[136px] rounded-lg border-slate-200 bg-slate-50 px-4 py-5 text-sm leading-5 text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";

function InfoIcon() {
  return <Info className="h-[15px] w-[15px] shrink-0 text-slate-500" strokeWidth={1.8} />;
}

function SectionHeading({
  title,
  description,
  required,
}: {
  title: string;
  description: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <h3 className="text-sm font-bold leading-5 text-slate-800">
          {title}
          {required && <span className="ml-1 text-sm font-bold text-slate-500">*</span>}
        </h3>
        <InfoIcon />
      </div>
      <p className="text-sm leading-5 text-slate-500">{description}</p>
    </div>
  );
}

function ChoiceCard({
  selected,
  onClick,
  disabled,
  label,
  type,
  name,
  id,
}: {
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
  label: string;
  type: "radio" | "checkbox";
  name?: string;
  id: string;
}) {
  const isRadio = type === "radio";

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[56px] flex-1 items-center gap-3 rounded-2xl border p-4 transition-colors",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        selected
          ? "border-primary/20 bg-primary/5"
          : "border-slate-200 bg-white hover:border-slate-300"
      )}
    >
      <input
        id={id}
        type={type}
        name={name}
        className="sr-only"
        checked={selected}
        onChange={onClick}
        disabled={disabled}
      />
      <span
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center border",
          isRadio ? "rounded-full" : "rounded-[4px]",
          selected ? "border-slate-500 bg-primary" : isRadio ? "border-gray-500 bg-white" : "border-slate-300 bg-white"
        )}
        aria-hidden="true"
      >
        {selected && <span className={cn("bg-white", isRadio ? "h-2 w-2 rounded-full" : "h-2.5 w-2.5 rounded-[2px]")} />}
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

export function RiskManagementCard({ onCollapse }: RiskManagementCardProps) {
  const {
    riskManagementData,
    updateRiskManagementData,
    riskManagementCompletion,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Risk management data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTimeHorizonChange = (value: string, checked: boolean) => {
    const current = riskManagementData.timeHorizon || [];
    if (checked) {
      updateRiskManagementData({ timeHorizon: [...current, value] });
    } else {
      updateRiskManagementData({ timeHorizon: current.filter((v) => v !== value) });
    }
  };

  const showRiskDetails = riskManagementData.climateRisksIdentified === true;

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title="Climate risk management"
        description={"Identify and describe climate-related risks to your business"}
        onClose={onCollapse}
        onSave={handleSave}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Environment"}
      />

      {disabled && <LockedBanner />}

      <SectionCard
        header={
          <SectionHeading
            title={"Climate-Related Risks"}
            description={"Have you identified climate-related risks in your business (physical hazards like floods/heat, or transition risks)?"}
            required
          />
        }
      >
        <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
          <ChoiceCard
            id="risk_yes"
            type="radio"
            name="climate_risks"
            selected={riskManagementData.climateRisksIdentified === true}
            onClick={() => updateRiskManagementData({ climateRisksIdentified: true })}
            disabled={disabled}
            label={"Yes"}
          />
          <ChoiceCard
            id="risk_no"
            type="radio"
            name="climate_risks"
            selected={riskManagementData.climateRisksIdentified === false}
            onClick={() => updateRiskManagementData({ climateRisksIdentified: false })}
            disabled={disabled}
            label={"No"}
          />
        </div>
      </SectionCard>

      {showRiskDetails && (
        <>
          <SectionCard
            header={
              <SectionHeading
                title={"1) Main Hazards / Events (If Yes)"}
                description={"Describe the main climate-related hazards or extreme weather events"}
              />
            }
          >
            <Textarea
              placeholder={"e.g. Flooding in rainy season, prolonged droughts, heat stress affecting operations, erratic rainfall patterns, etc."}
              className={TEXTAREA_CLASS}
              value={riskManagementData.mainHazards || ""}
              onChange={(e) => updateRiskManagementData({ mainHazards: e.target.value })}
              disabled={disabled}
            />
          </SectionCard>

          <SectionCard
            header={
              <SectionHeading
                title={"2) What's Exposed or Sensitive?"}
                description={"What aspects of your business are at risk or sensitive to these hazards?"}
              />
            }
          >
            <Textarea
              placeholder={"e.g. Crops/livestock in agriculture, water availability for operations, electricity supply, transportation routes, assets in flood-prone areas, supply chain disruptions, etc."}
              className={TEXTAREA_CLASS}
              value={riskManagementData.riskAssessmentProcess || ""}
              onChange={(e) => updateRiskManagementData({ riskAssessmentProcess: e.target.value })}
              disabled={disabled}
            />
          </SectionCard>

          <SectionCard
            header={
              <SectionHeading
                title={"3) Time Horizon"}
                description={"What is the expected time frame for these risks to impact your business?"}
              />
            }
          >
            <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2 xl:grid-cols-3">
              <ChoiceCard
                id="short_term"
                type="checkbox"
                selected={(riskManagementData.timeHorizon || []).includes("short-term")}
                onClick={() =>
                  handleTimeHorizonChange(
                    "short-term",
                    !(riskManagementData.timeHorizon || []).includes("short-term")
                  )
                }
                disabled={disabled}
                label={"Short term (0-2 years)"}
              />
              <ChoiceCard
                id="medium_term"
                type="checkbox"
                selected={(riskManagementData.timeHorizon || []).includes("medium-term")}
                onClick={() =>
                  handleTimeHorizonChange(
                    "medium-term",
                    !(riskManagementData.timeHorizon || []).includes("medium-term")
                  )
                }
                disabled={disabled}
                label={"Medium term (2-5 years)"}
              />
              <ChoiceCard
                id="long_term"
                type="checkbox"
                selected={(riskManagementData.timeHorizon || []).includes("long-term")}
                onClick={() =>
                  handleTimeHorizonChange(
                    "long-term",
                    !(riskManagementData.timeHorizon || []).includes("long-term")
                  )
                }
                disabled={disabled}
                label={"Long term (5+ years)"}
              />
            </div>
          </SectionCard>

          <SectionCard

            header={
              <SectionHeading
                title={"4) Adaptation Actions"}
                description={"What actions are you taking to adapt or reduce these risks?"}
              />
            }
          >
            <Textarea
              placeholder={"e.g. Improved water management systems, diversified crop varieties, emergency disaster plans, renewable energy adoption, insurance coverage, early warning systems, etc."}
              className={TEXTAREA_CLASS}
              value={riskManagementData.adaptationActions || ""}
              onChange={(e) => updateRiskManagementData({ adaptationActions: e.target.value })}
              disabled={disabled}
            />
          </SectionCard>

          <SectionCard
            header={
              <SectionHeading
                title={"5) Risk Level (Optional)"}
                description={"How would you assess the overall severity of these risks?"}
              />
            }
          >
            <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-3">
              <ChoiceCard
                id="risk_high"
                type="radio"
                name="risk_level"
                selected={riskManagementData.riskLevel === "high"}
                onClick={() => updateRiskManagementData({ riskLevel: "high" })}
                disabled={disabled}
                label={"High"}
              />
              <ChoiceCard
                id="risk_medium"
                type="radio"
                name="risk_level"
                selected={riskManagementData.riskLevel === "medium"}
                onClick={() => updateRiskManagementData({ riskLevel: "medium" })}
                disabled={disabled}
                label={"Medium"}
              />
              <ChoiceCard
                id="risk_low"
                type="radio"
                name="risk_level"
                selected={riskManagementData.riskLevel === "low"}
                onClick={() => updateRiskManagementData({ riskLevel: "low" })}
                disabled={disabled}
                label={"Low"}
              />
            </div>
          </SectionCard>

          <SectionCard
            header={
              <SectionHeading
                title={"6) Potential Adverse Effects (Optional)"}
                description={"Briefly describe potential negative impacts if risks materialize"}
              />
            }
          >
            <Textarea
              placeholder={"e.g. Reduced productivity, increased operating costs, supply chain disruptions, loss of income, asset damage, etc."}
              className={TEXTAREA_CLASS}
              value={riskManagementData.potentialEffects || ""}
              onChange={(e) => updateRiskManagementData({ potentialEffects: e.target.value })}
              disabled={disabled}
            />
          </SectionCard>
        </>
      )}

      <TransitionMeasuresBlock topicId="risk-management" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={riskManagementCompletion} />
    </div>
  );
}
